import { Directive, ElementRef, HostListener, OnDestroy, OnInit, Optional, Self } from "@angular/core";
import { NgControl } from "@angular/forms";
import { Subscription, fromEvent } from "rxjs";
import {
  TIME_FORMAT_CHANGE_EVENT,
  TimeFormatPreference,
  getUserTimeFormatPreference,
  normalizeTypedTimeInput,
  parseDisplayTimeTo24h,
  toDisplayTime,
} from "../utils/time-format-preference";

@Directive({
  selector: "input[type=time][ngModel],input[type=time][formControlName],input[type=time][formControl]",
})
export class TimeFormatInputDirective implements OnInit, OnDestroy {
  private sub = new Subscription();

  constructor(
    private host: ElementRef<HTMLInputElement>,
    @Optional() @Self() private ngControl: NgControl
  ) {}

  ngOnInit(): void {
    this.prepareInput();

    if (this.ngControl?.valueChanges) {
      this.sub.add(
        this.ngControl.valueChanges.subscribe((value) => {
          this.updateDisplayedValue(value);
        })
      );
    }

    this.sub.add(
      fromEvent(window, TIME_FORMAT_CHANGE_EVENT).subscribe(() => {
        this.updateDisplayedValue(this.ngControl?.value);
        this.updatePlaceholder();
      })
    );

    this.updateDisplayedValue(this.ngControl?.value);
    this.updatePlaceholder();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  @HostListener("input", ["$event.target.value"])
  onInput(value: string): void {
    this.applyTypedFormatting(value);
  }

  @HostListener("blur")
  onBlur(): void {
    const input = this.host.nativeElement;
    const normalized = normalizeTypedTimeInput(input.value);
    const parsed = normalized.canonical ?? parseDisplayTimeTo24h(input.value);

    if (parsed === null) {
      this.updateDisplayedValue(this.ngControl?.value);
      return;
    }

    if (this.ngControl?.control) {
      this.ngControl.control.setValue(parsed);
      this.ngControl.control.markAsTouched();
    }

    input.value = toDisplayTime(parsed);
  }

  private prepareInput(): void {
    const input = this.host.nativeElement;
    input.type = "text";
    input.autocomplete = "off";
    input.spellcheck = false;
    input.inputMode = "text";
  }

  private updateDisplayedValue(value: string): void {
    const input = this.host.nativeElement;
    input.value = toDisplayTime(value || "");
  }

  private updatePlaceholder(): void {
    const input = this.host.nativeElement;
    const format: TimeFormatPreference = getUserTimeFormatPreference();
    input.placeholder = format === "12h" ? "hh:mm AM" : "HH:mm";
  }

  private applyTypedFormatting(value: string): void {
    const input = this.host.nativeElement;
    const normalized = normalizeTypedTimeInput(value);

    input.value = normalized.display;

    if (!this.ngControl?.control) {
      return;
    }

    if (normalized.canonical === "") {
      this.ngControl.control.setValue("", { emitEvent: false });
      return;
    }

    if (normalized.canonical !== null) {
      this.ngControl.control.setValue(normalized.canonical, { emitEvent: false });
    }
  }
}
